import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Lock, Mail, Loader2, UserPlus, LogIn, KeyRound, ArrowLeft, Star } from 'lucide-react';
import { Logo } from './Logo';

type AuthMode = 'LOGIN' | 'SIGNUP' | 'FORGOT_PASSWORD';

export const Auth: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('LOGIN');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [carPlate, setCarPlate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [inviterName, setInviterName] = useState<string | null>(null);

  useEffect(() => {
     const params = new URLSearchParams(window.location.search);
     const refId = params.get('ref');
     const refName = params.get('name');
     
     if (refId) {
         localStorage.setItem('driver_score_referred_by', refId);
     }
     
     if (refName) {
         setInviterName(decodeURIComponent(refName));
     } else if (refId) {
         setInviterName('Um colega');
     }
  }, []);

  const translateError = (msg: string) => {
    if (msg.includes('Invalid login credentials')) return 'Email ou senha incorretos.';
    if (msg.includes('User already registered')) return 'Este email já está cadastrado.';
    if (msg.includes('Password should be at least')) return 'A senha deve ter no mínimo 6 caracteres.';
    if (msg.includes('Email not confirmed')) return 'Email não confirmado. Verifique sua caixa de entrada e SPAM.';
    if (msg.includes('Rate limit exceeded')) return 'Muitas tentativas. Aguarde um pouco.';
    if (msg.includes('identity_already_exists') || msg.includes('assigned to another user')) return 'Este email já está vinculado a outra forma de login (Google ou Email/Senha).';
    return 'Ocorreu um erro. Tente novamente.';
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setMessage(null);

    if (authMode !== 'FORGOT_PASSWORD' && password.length > 72) {
        setError('Senha muito longa (máximo 72 caracteres).');
        setIsLoading(false);
        return;
    }

    const redirectUrl = window.location.origin;

    try {
      if (authMode === 'SIGNUP') {
        const storedRef = localStorage.getItem('driver_score_referred_by');
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl, 
            data: {
              full_name: name,
              car_plate: carPlate,
              referred_by: storedRef || null
            }
          }
        });
        if (error) throw error;
        setMessage('Cadastro realizado! Um link de confirmação foi enviado para seu email. Verifique também a caixa de SPAM.');
      
      } else if (authMode === 'LOGIN') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      
      } else if (authMode === 'FORGOT_PASSWORD') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: redirectUrl,
        });
        if (error) throw error;
        setMessage('Se o email estiver cadastrado, você receberá um link para redefinir a senha. Verifique o SPAM.');
      }

    } catch (err: any) {
      setError(translateError(err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    const redirectUrl = window.location.origin;

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });
      if (error) throw error;
    } catch (err: any) {
      console.error("Google Login Error:", err);
      setError("Erro ao conectar com Google. Tente novamente.");
      setIsLoading(false);
    }
  };

  const switchMode = (mode: AuthMode) => {
    setAuthMode(mode);
    setError(null);
    setMessage(null);
    if (mode === 'FORGOT_PASSWORD') setPassword('');
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-start p-4 font-sans selection:bg-orange-500/30 overflow-y-auto w-full relative">
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-[100px] -z-10 animate-pulse-gentle"></div>
      <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[100px] -z-10 animate-pulse-gentle delay-1000"></div>

      {/* Landing Intro Section */}
      <div className="max-w-lg w-full text-center mt-8 md:mt-12 mb-6 animate-fade-in relative z-10 flex flex-col items-center">
        <Logo className="w-24 h-24 mb-6 drop-shadow-2xl" />
        <h1 className="text-4xl md:text-5xl font-black text-slate-100 tracking-tighter mb-4">Driver Score</h1>
        <p className="text-slate-300 text-sm md:text-base font-medium mb-6 px-4">
          A calculadora inteligente e ferramenta de logística desenvolvida para otimizar os ganhos, adiantamentos e o roteamento diário de motoristas parceiros independentes.
        </p>
        <div className="flex flex-wrap justify-center gap-2 mb-2 px-4">
            <span className="bg-slate-800/80 border border-slate-700 text-slate-300 text-xs px-3 py-1 rounded-full font-medium">Controle Financeiro</span>
            <span className="bg-slate-800/80 border border-slate-700 text-slate-300 text-xs px-3 py-1 rounded-full font-medium">Rankings da Frota</span>
            <span className="bg-slate-800/80 border border-slate-700 text-slate-300 text-xs px-3 py-1 rounded-full font-medium">Localização GPS</span>
        </div>
      </div>

      {/* Auth Card */}
      <div className="bg-slate-800/80 backdrop-blur-2xl p-8 md:p-10 rounded-[2rem] shadow-2xl max-w-md w-full border border-slate-700/50 animate-scale-in relative z-10">
        
        {inviterName && (
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 p-2 text-center shadow-lg border-b border-blue-400/30">
                <p className="text-xs font-black text-white flex items-center justify-center gap-2">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    CONVITE ACEITO: {inviterName}
                </p>
            </div>
        )}

        <div className={`mb-8 text-center ${inviterName ? 'mt-8' : ''}`}>
           <h2 className="text-2xl font-bold text-slate-100 tracking-tight">Acesso à Plataforma</h2>
           <p className="text-slate-400 text-sm mt-1">{authMode === 'SIGNUP' ? 'Crie sua conta para começar' : 'Entre com seus dados salvos'}</p>
        </div>

        <form onSubmit={handleAuth} className="space-y-6">
          {authMode === 'SIGNUP' && (
            <>
              <div className="space-y-2 animate-slide-down">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Nome Completo</label>
                <div className="relative group">
                  <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-orange-500 transition-colors" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-2xl pl-12 pr-4 py-4 text-slate-100 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 outline-none transition-all placeholder:text-slate-500 font-medium text-sm"
                    placeholder="Seu nome"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Email de Acesso</label>
            <div className="relative group">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-orange-500 transition-colors" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700/50 rounded-2xl pl-12 pr-4 py-4 text-slate-100 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 outline-none transition-all placeholder:text-slate-500 font-medium text-sm"
                placeholder="seu@email.com"
                maxLength={100}
              />
            </div>
          </div>
          
          {authMode !== 'FORGOT_PASSWORD' && (
            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Senha</label>
                {authMode === 'LOGIN' && (
                  <button 
                    type="button"
                    onClick={() => switchMode('FORGOT_PASSWORD')}
                    className="text-[10px] font-black text-orange-500 hover:text-orange-400 uppercase tracking-wider"
                  >
                    Recuperar Acesso?
                  </button>
                )}
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-orange-500 transition-colors" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-2xl pl-12 pr-4 py-4 text-slate-100 focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 outline-none transition-all placeholder:text-slate-500 font-medium text-sm"
                  placeholder="••••••••"
                  minLength={6}
                  maxLength={72}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-red-100 p-3 rounded-xl text-xs font-bold text-center animate-shake">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-900/40 border border-green-500/50 text-green-100 p-3 rounded-xl text-xs font-bold text-center">
              {message}
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 disabled:opacity-50 text-white font-black py-4 rounded-2xl transition-all shadow-xl shadow-orange-900/20 active:scale-95 flex items-center justify-center gap-3 text-sm uppercase tracking-widest"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : authMode === 'SIGNUP' ? (
                <>
                  <UserPlus className="w-5 h-5" />
                  Criar Conta
                </>
              ) : authMode === 'FORGOT_PASSWORD' ? (
                <>
                  <KeyRound className="w-5 h-5" />
                  Enviar Email
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Entrar
                </>
              )}
            </button>
          </div>
        </form>

        {authMode !== 'FORGOT_PASSWORD' && (
            <div className="mt-6 flex justify-center pb-6 border-b border-slate-700/50">
               {authMode === 'LOGIN' ? (
                   <button onClick={() => switchMode('SIGNUP')} className="text-xs text-slate-400 hover:text-slate-100 transition-colors bg-slate-800/50 py-2.5 px-6 rounded-full border border-slate-700 cursor-pointer w-full">
                       Não tem conta? <span className="font-bold text-orange-500 ml-1">Quero me cadastrar</span>
                   </button>
               ) : (
                   <button onClick={() => switchMode('LOGIN')} className="text-xs text-slate-400 hover:text-slate-100 transition-colors bg-slate-800/50 py-2.5 px-6 rounded-full border border-slate-700 cursor-pointer w-full">
                       Já tem conta? <span className="font-bold text-orange-500 ml-1">Fazer login</span>
                   </button>
               )}
            </div>
        )}

        {/* Hide Google button temporarily since it's not working */}
        {/* {authMode !== 'FORGOT_PASSWORD' && (
            <div className="pt-6">
                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                    className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-3.5 rounded-2xl transition-all shadow-xl flex items-center justify-center gap-3 border border-slate-200 active:scale-95 text-sm"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continuar com Google
                </button>
            </div>
        )} */}

        {authMode === 'FORGOT_PASSWORD' && (
            <div className="mt-8 text-center border-t border-slate-700/50 pt-6">
               <button
                 onClick={() => switchMode('LOGIN')}
                 className="text-slate-400 hover:text-slate-100 text-xs flex items-center justify-center gap-2 mx-auto font-black uppercase tracking-widest transition-colors"
               >
                 <ArrowLeft className="w-3 h-3" />
                 Voltar
               </button>
            </div>
        )}

      </div>

      {/* Footer / Legal Links */}
      <footer className="mt-8 mb-6 py-6 text-center w-full max-w-md relative z-10 flex flex-col items-center gap-4">
         <p className="text-[10px] text-slate-600 uppercase tracking-[0.2em] font-black">
           A ROMANOSV3 STUDIO Project
         </p>
         <div className="flex items-center gap-4 text-xs font-medium text-slate-400">
           <a href="/privacidade" className="hover:text-orange-400 transition-colors underline underline-offset-4 decoration-slate-700 hover:decoration-orange-400">Política de Privacidade</a>
           <span className="text-slate-700">•</span>
           <a href="/privacidade" className="hover:text-orange-400 transition-colors underline underline-offset-4 decoration-slate-700 hover:decoration-orange-400">Termos de Serviço</a>
         </div>
      </footer>
    </div>
  );
};
